import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import SignatureCanvas from 'react-signature-canvas';
import { faPen, faKeyboard } from '@fortawesome/pro-light-svg-icons';
import { White } from '../../constants/Colors';
import ThemeContext from '../../theme/ThemeContext';

import Modal from './Modal';
import Button from './Button';

const StyledWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const StyledSignatureMeasurer = styled.div`
  position: relative;
  margin-bottom: 8px;
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  background-color: ${White};
  border-radius: ${(props) => props.theme.borderRadius}px;
  border: 1px solid ${(props) => props.theme.borderColor};
  max-height: 190px;
`;

const StyledSignatureLine = styled.div`
  position: absolute;
  border-bottom: 1px dashed ${(props) => props.theme.borderColor};
  bottom: 54px;
  width: 80%;
  margin-right: auto;
  margin-left: auto;
`;

const StyledSubText = styled.span`
  font-size: 11px;
  color: ${(props) => props.theme.fontColorSecondary};
`;

const StyledActionsWrapper = styled.div`
  margin-bottom: 8px;
  justify-content: space-between;
  align-items: center;
  display: flex;
  flex-direction: row;
`;

const StyledInputWrapper = styled.div`
  align-self: flex-start;
`;

const StyledInput = styled.input`
  display: flex;
  flex: 1;
  background-color: ${(props) => props.theme.backgroundColor};
  font-size: ${(props) => props.theme.fontSize};
  color: ${(props) => props.theme.fontColorPrimary};
  border-radius: ${(props) => props.theme.borderRadius}px;
  border: ${(props) => props.theme.borderWidth}px solid ${(props) => props.theme.borderColor};
  padding: 8px;
  transition: all 0.15s ease-in-out;

  &:hover, &:focus {
    outline: unset; 
    border-style: solid;
    border-width: 1px !important;
    border-right-color: ${(props) => props.theme.primaryColor} !important;
    border-left-color: ${(props) => props.theme.primaryColor} !important;
    border-color: ${(props) => props.theme.primaryColor};
  }

`;

const StyledButtonsWrapper = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  gap: 8px;
`;

const StyledSignatureArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;
const StyledSignatureOptions = styled.div`
  display: flex;
  gap: 5px;
  flex-direction: row;
  padding-right: 5px;
  padding-bottom: 8px;
  `;

class SignatureModal extends PureComponent {

  static contextType = ThemeContext

  static propTypes = {

    id: PropTypes.string,
    display: PropTypes.bool,
    primaryDisplayOnly: PropTypes.bool,

    //Action handlers
    onSubmit: PropTypes.func,
    onHide: PropTypes.func,
    onRef: PropTypes.func, //Used for testing purposes
    width: PropTypes.number,

  }

  constructor(props) {
    super(props);
    this.state = {
      changed: false,
      confirmingClear: false,
      signatureOffsets: null,
      signer: '',
      activeOption: 'draw',
      inputValue: '',

    };
  }

  render() {

    const {
      id,
      disabled,
      onRef,
      display,
      onHide,
      primaryDisplayOnly,
      width,
    } = this.props;
    const { changed, confirmingClear, signatureOffsets, activeOption, inputValue } = this.state;

    let bodyNode = null;

    if (signatureOffsets) {
      bodyNode = (
        <SignatureCanvas
          penColor="black"
          onEnd={() => {
            this.setState({ changed: true });
          }}
          ref={(e) => {
            if (!e) return;
            if (onRef) onRef(e);
            if (activeOption === 'type' && e) {
              e.off();
            } else if (activeOption === 'draw' && e) {
              e.on();
            }
            this._canvasRef = e;
          }}
          testId={`${id}-signature-field-canvas`}
          canvasProps={{
            width: signatureOffsets.width,
            height: signatureOffsets.height,
          }}
        />
      );
    }

    const cancelClearButton = !confirmingClear ? null : (
      <Button
        size="sm"
        styles={{ marginLeft: '8px' }}
        onClick={this.handleClearCancel}
        text="Cancel"
      />
    );

    const clearButton = changed && !disabled ? (
      <Button
        size="sm"
        danger
        onClick={confirmingClear ? this.handleClear : this.handleClearConfirm}
        text={confirmingClear ? 'Confirm Clear' : 'Clear'}
      />
    ) : null;

    const signatureButtonOption = (option) => {
      return (
        <Button
          icon={option === 'type' ? faKeyboard : faPen}
          text={option === 'type' ? 'Type  ' : 'Draw '}
          selected={activeOption === option}
          onClick={() => this.handleOptionChange(option)}
        />
      );
    };

    const legalVerbiage = primaryDisplayOnly ? null : (
      <StyledSubText theme={this.context}>
        The parties agree that this agreement may be electronically signed. The parties agree that the
        electronic signatures appearing on this agreement are the same as handwritten signatures for
        the purposes of validity, enforceability, and admissibility.
      </StyledSubText>
    );

    const canvasInput = !disabled ? (
      <StyledInputWrapper>
        <StyledInput
          theme={this.context}
          maxLength={60}
          disabled={activeOption === 'draw'}
          placeholder="Type signature"
          value={inputValue}
          onChange={this.handleCanvasFill}
          onKeyDown={this.handleKeyDown}
        />
      </StyledInputWrapper>
    ) : null;

    return (
      <Modal
        title="Signature"
        width={width || 320}
        isOpen={display}
        onHide={onHide}
        submitText="Save"
        onSubmit={this.handleSave}
        overlayStyles={{ zIndex: 10000000000 }}
      >
        <StyledWrapper>
          <StyledSignatureOptions>
            {signatureButtonOption('draw')}
            {signatureButtonOption('type')}
          </StyledSignatureOptions>
          <StyledSignatureArea>
            <StyledSignatureMeasurer
              theme={this.context}
              ref={(e) => {
                if (e && !signatureOffsets) {
                  this.setState({ signatureOffsets: e.getBoundingClientRect() });
                }
              }}
            >
              {bodyNode}
              <StyledSignatureLine theme={this.context} />
              <StyledButtonsWrapper>
                {clearButton}
                {cancelClearButton}
              </StyledButtonsWrapper>
            </StyledSignatureMeasurer>
            <StyledActionsWrapper>
              { activeOption === 'type' ? canvasInput : null }
            </StyledActionsWrapper>
            {legalVerbiage}
          </StyledSignatureArea>
        </StyledWrapper>
      </Modal>
    );

  }

  handleOptionChange = (option) => {
    this.setState(
      {
        activeOption: option,
        inputValue: option === 'draw' ? '' : this.inputValue, // Clear input if switching to draw
        changed: false,
      },
      () => {
        if (this._canvasRef) {
          this._canvasRef.clear(); // Always clear the canvas
        }
      }
    );
  };

  handleKeyDown = (e) => {

    const canvas = this._canvasRef.getCanvas();
    const ctx = canvas.getContext('2d');

    /**
     * Logic below clears the typed signature when backspace is pressed
     */
    if (e.keyCode === 8) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

  }

  handleCanvasFill = (e) => {
    const text = e.target.value;

    const { signatureOffsets } = this.state;

    // Backspacing doesn't work for native canvas so we pre-clear
    if (text.length < 1) this.handleClear();

    // Use original underlying canvas ref to use browser native
    // .fillText as react-signature-canvas doesn't support it
    const canvas = this._canvasRef.getCanvas();
    const ctx = canvas.getContext('2d');
    //Define the max and min font size
    const maxFontSize = 80;
    const minFontSize = 10;
    let fontSize = maxFontSize;

    // Brush Script MT is the only natively cross browser
    // supported cursive font
    ctx.font = `${fontSize}px Brush Script MT`;
    //Measure the text width
    let textWidth = ctx.measureText(text).width;

    const canvasPadding = canvas.width - 15 * 2;

    // Adjust font size to fit within the padded area
    while (textWidth > canvasPadding && fontSize > minFontSize) {
      fontSize--;
      ctx.font = `${fontSize}px Brush Script MT`;
      textWidth = ctx.measureText(text).width;
    }

    const x = (canvas.width - textWidth) / 2;
    // Clear previous text
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Draw the text at the calculated position
    ctx.fillText(text, x, signatureOffsets.height / 2.2);

    this.setState({ signer: text, changed: true, inputValue: text });
  }

  handleClearCancel = () => {
    this.setState({ confirmingClear: false });
  }

  handleClearConfirm = () => {
    this.setState({ confirmingClear: true });
  }

  handleClear = () => {

    const { fieldId, onChange, identifier } = this.props;

    if (this._canvasRef) this._canvasRef.clear();

    if (onChange) onChange(fieldId, identifier, '');

    this.setState({ changed: false, confirmingClear: false, inputValue: '' });

  }

  handleSave = () => {

    const { onSubmit } = this.props;
    const { signer, changed } = this.state;

    const canvas = this._canvasRef ? this._canvasRef.getCanvas() : null;

    /**
     * We are checking if changed is true to prevent empty signature
     * from being saved as the field value.
     */
    const dataUrl = canvas && changed ? canvas.toDataURL() : '';

    if (onSubmit) onSubmit(dataUrl, signer);

    this.setState({ changed: false, inputValue: '' });

  }

}

export default SignatureModal;
